import { useEffect, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { useGetInsAuditLogsQuery } from '@/core/services/ins-audit-log'
import { InsAuditLog, InsOperation } from '@/core/models/ins-audit.model'
export const INS_AUDIT_LOGS_PAGE_SIZE = 10

export type UseInsAuditLogsResult = {
  page: number
  pageCount: number
  total: number
  logs: InsAuditLog[]
  isLoading: boolean
  isError: boolean
  operation?: InsOperation
  searchQuery: string
  handleOperationChange: (nextOperation?: InsOperation) => void
  handleSearchChange: (query: string) => void
  handleReset: () => void
  setPage: (page: number) => void
}

export function useInsAuditLogs(): UseInsAuditLogsResult {
  const [page, setPage] = useState(1)
  const [operation, setOperation] = useState<InsOperation | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounceValue(searchQuery.trim(), 300)

  const { data, isLoading, isError } = useGetInsAuditLogsQuery({
    operation,
    search: debouncedSearchQuery || undefined,
    limit: INS_AUDIT_LOGS_PAGE_SIZE,
    offset: (page - 1) * INS_AUDIT_LOGS_PAGE_SIZE,
  })

  const logs: InsAuditLog[] = data?.logs ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / INS_AUDIT_LOGS_PAGE_SIZE))

  useEffect(() => {
    setPage((previousPage) => Math.min(previousPage, pageCount))
  }, [pageCount])

  const handleOperationChange = (nextOperation?: InsOperation) => {
    if (nextOperation && !Object.values(InsOperation).includes(nextOperation)) {
      setPage(1)
      setOperation(undefined)
      return
    }
    setPage(1)
    setOperation(nextOperation)
  }

  const handleSearchChange = (query: string) => {
    setPage(1)
    setSearchQuery(query)
  }

  const handleReset = () => {
    setPage(1)
    setOperation(undefined)
    setSearchQuery('')
  }

  return {
    page,
    pageCount,
    total,
    logs,
    isLoading,
    isError,
    operation,
    searchQuery,
    handleOperationChange,
    handleSearchChange,
    handleReset,
    setPage,
  }
}

